import { ChevronDown } from "lucide-react";
import type { CandidateMachineResult } from "@opus-dt/shared-types";
import { cn } from "@/lib/utils";
import { magnitudePct } from "./chart-scale";
import { WINNER_COLOR } from "./IndicatorCompare";

interface MachineThroughputProps {
  base: CandidateMachineResult[];
  winner: CandidateMachineResult[];
  winnerLabel: string;
}

interface Pair {
  name: string;
  base: number | null;
  winner: number | null;
  /** Declarado pelo motor. */
  bottleneck: boolean;
  /** Menor vazão da configuração recomendada — a evidência visual de onde a linha trava. */
  slowest: boolean;
}

const nf = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 });

/** Pareia por máquina preservando a ordem da base; máquina só do vencedor entra ao final. */
export function pairMachines(
  base: CandidateMachineResult[],
  winner: CandidateMachineResult[]
): Pair[] {
  const byId = new Map(winner.map((m) => [m.machineId, m]));
  const pairs: Pair[] = base.map((b) => {
    const w = byId.get(b.machineId);
    byId.delete(b.machineId);
    return {
      name: b.name,
      base: b.throughputUnH,
      winner: w?.throughputUnH ?? null,
      // O gargalo que importa é o da configuração RECOMENDADA — é a que se pretende adotar.
      bottleneck: w?.isBottleneck ?? b.isBottleneck,
      slowest: false,
    };
  });
  for (const w of byId.values()) {
    pairs.push({
      name: w.name,
      base: null,
      winner: w.throughputUnH,
      bottleneck: w.isBottleneck,
      slowest: false,
    });
  }
  const min = Math.min(...pairs.map((p) => p.winner ?? Number.POSITIVE_INFINITY));
  return pairs.map((p) => ({ ...p, slowest: p.winner != null && p.winner === min }));
}

/**
 * Vazão efetiva por máquina, base × recomendado.
 *
 * O agregado responde "quanto melhorou"; esta é a única superfície que responde "onde". Numa linha
 * de máquinas paralelas quase idênticas isso decide investimento: mexer nas três juntas ou numa só
 * não é a mesma conta. O eixo começa no ZERO porque vazão tem zero real — aqui a proporção entre
 * as barras significa alguma coisa, ao contrário dos indicadores em pp.
 */
export function MachineThroughput({ base, winner, winnerLabel }: MachineThroughputProps) {
  const pairs = pairMachines(base, winner);
  if (pairs.length === 0) return null;
  const max = Math.max(...pairs.flatMap((p) => [p.base ?? 0, p.winner ?? 0]));
  const totalBase = sum(pairs.map((p) => p.base));
  const totalWinner = sum(pairs.map((p) => p.winner));
  const gainPct = totalBase > 0 && totalWinner > 0 ? (totalWinner / totalBase - 1) * 100 : null;
  // Um selo em CADA máquina não é evidência — é ruído. Quando o motor aponta todas (arranjo em
  // paralelo, vazões quase iguais), o fato é sobre o conjunto e vira uma frase, não N etiquetas.
  const allBottleneck = pairs.every((p) => p.bottleneck);

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-muted-foreground">
        vazão efetiva (un/h) · eixo começa em zero
      </p>
      {pairs.map((p) => (
        <div key={p.name} className="border-b border-border-subtle pb-2 last:border-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] font-medium">{p.name}</span>
            {p.slowest ? (
              <span className="inline-flex items-center gap-0.5 rounded bg-rd-warning-50 px-1.5 text-[10px] font-medium text-rd-warning-700">
                <ChevronDown className="h-3 w-3" aria-hidden />
                menor vazão
              </span>
            ) : null}
            {p.bottleneck && !allBottleneck ? (
              <span className="shrink-0 rounded bg-rd-danger-50 px-1.5 text-[10px] font-medium text-rd-danger-700">
                gargalo
              </span>
            ) : null}
          </div>
          <Row value={p.base} max={max} label="Base" />
          <Row value={p.winner} max={max} label={winnerLabel} color={WINNER_COLOR} />
        </div>
      ))}

      {/* Um selo em CADA máquina não é evidência — é ruído. Quando o motor aponta todas (arranjo
          em paralelo, vazões quase iguais), o fato é sobre o conjunto: vira frase, não N etiquetas. */}
      {allBottleneck ? (
        <p className="rounded-md border border-rd-danger-100 bg-rd-danger-50 px-2.5 py-2 text-[11px] leading-relaxed text-rd-danger-700">
          <b>As {pairs.length} máquinas limitam a linha juntas.</b> Elas trabalham em paralelo com
          vazões quase iguais — mexer em uma só desloca pouco o total. O ganho vem de mudar todas.
        </p>
      ) : null}

      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-t border-border-subtle pt-2 text-[11px]">
        <span className="text-muted-foreground">Linha (soma das máquinas)</span>
        <span className="font-mono tabular-nums">
          {nf.format(totalBase)} → {nf.format(totalWinner)} un/h
          {gainPct != null ? (
            <span
              className={cn(
                "ml-2 font-semibold",
                gainPct >= 0 ? "text-rd-success-500" : "text-rd-danger-500"
              )}
            >
              {gainPct >= 0 ? "+" : "−"}
              {Math.abs(gainPct).toFixed(1)}%
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );
}

function sum(values: Array<number | null>): number {
  return values.reduce<number>((acc, v) => acc + (v ?? 0), 0);
}

function Row({
  value,
  max,
  label,
  color,
}: {
  value: number | null;
  max: number;
  label: string;
  color?: string;
}) {
  return (
    <div
      className="mt-1 flex items-center gap-2"
      role="img"
      aria-label={`${label}: ${value == null ? "sem dado" : `${nf.format(value)} unidades por hora`}`}
    >
      {/* Sem o nome da série ao lado, duas barras empilhadas viram um enfeite: o leitor não sabe
          qual é a atual e qual é a proposta sem recorrer à legenda no rodapé do card. */}
      <span className="w-[74px] shrink-0 text-[10px] text-muted-foreground">{label}</span>
      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-bg-sunken">
        <div
          className={cn(
            "h-full w-[var(--bar-w)] rounded-full",
            color ? "bg-[var(--bar-color)]" : "bg-text-secondary"
          )}
          style={
            {
              "--bar-w": `${magnitudePct(value ?? 0, max)}%`,
              ...(color ? { "--bar-color": color } : {}),
            } as React.CSSProperties
          }
        />
      </div>
      <span className="w-12 shrink-0 text-right font-mono text-[11px] tabular-nums text-muted-foreground">
        {value == null ? "—" : nf.format(value)}
      </span>
    </div>
  );
}
